import { useCallback, useEffect, useMemo, useRef } from "react";

const WIZARD_HISTORY_STEP_KEY = "__widerrufWizardStep";
const WIZARD_HISTORY_STEPS = ["customer", "manual", "order", "excluded", "confirmation"];

export function useWizardHistory(wizard) {
  const { goToCustomer, goToStep, step } = wizard;
  const browserNavigationRef = useRef(false);
  const historyReadyRef = useRef(false);
  const initialStepRef = useRef(step);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    window.history.replaceState(wizardHistoryState(initialStepRef.current), "", window.location.href);
    historyReadyRef.current = true;

    const handlePopState = (event) => {
      const historyStep = readWizardHistoryStep(event.state);
      if (!historyStep) return;

      browserNavigationRef.current = true;
      goToStep(historyStep);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      historyReadyRef.current = false;
    };
  }, [goToStep]);

  useEffect(() => {
    if (!historyReadyRef.current || typeof window === "undefined") return;

    if (browserNavigationRef.current) {
      browserNavigationRef.current = false;
      return;
    }

    const currentHistoryStep = readWizardHistoryStep(window.history.state);
    if (currentHistoryStep === step) return;

    if (step === "confirmation") {
      window.history.replaceState(wizardHistoryState(step), "", window.location.href);
      return;
    }

    window.history.pushState(wizardHistoryState(step), "", window.location.href);
  }, [step]);

  const goBackToCustomer = useCallback(() => {
    if (typeof window !== "undefined" && step !== "customer" && readWizardHistoryStep(window.history.state) === step) {
      window.history.back();
      return;
    }

    goToCustomer();
  }, [goToCustomer, step]);

  return useMemo(
    () => ({
      ...wizard,
      goToCustomer: goBackToCustomer,
    }),
    [goBackToCustomer, wizard],
  );
}

function readWizardHistoryStep(state) {
  const step = state?.[WIZARD_HISTORY_STEP_KEY];
  return WIZARD_HISTORY_STEPS.includes(step) ? step : null;
}

function wizardHistoryState(step) {
  return {
    ...(window.history.state || {}),
    [WIZARD_HISTORY_STEP_KEY]: step,
  };
}
